import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { Select } from "components/ui/Select/Select";
import { Textarea } from "components/ui/Textarea/Textarea";
import type { PromptType } from "hooks/use-email-compose";
import { Sparkles } from "lucide-react";
import { type FC, useState } from "react";

import { MarginSelect } from "./MarginSelect";
import {
  ProposalInfoInput,
  type ProposalInfoTarget,
} from "./ProposalInfoInput";

/**
 * プロンプト種別の表示ラベルを返す (snake_case のオブジェクトキーを避けるため関数化).
 * @param {PromptType} type プロンプト種別
 * @return {string} 表示ラベル
 */
const promptTypeLabel = (type: PromptType): string => {
  switch (type) {
    case "reply_matter":
      return "人材を提案";
    case "reply_hr":
      return "案件を提案";
    case "inquiry":
      return "詳細問い合わせ";
    case "negotiate":
      return "単価交渉";
    case "schedule":
      return "日程調整";
    case "decline":
      return "お断り";
    case "followup":
      return "フォローアップ";
    case "custom":
      return "カスタム指示";
  }
};

const CUSTOM_PLACEHOLDER =
  "AI に伝えたい内容を短く入力 (例: 単価70万円で再提案、来週水曜の打合せ候補を提示、丁寧にお断り)";

export type AiComposeSectionProps = {
  /** プルダウンに出すプロンプト種別 */
  promptTypes: PromptType[];
  /** 初期選択するプロンプト種別 */
  defaultPromptType: PromptType;
  /** AI 生成中か */
  isGenerating: boolean;
  /** マージン(万円). reply_matter / reply_hr 選択時のみ表示 */
  margin: number;
  /**
   * マージン変更ハンドラ
   * @param {number} value 新しいマージン
   * @return {void}
   */
  onMarginChange: (value: number) => void;
  /** 提案情報. reply_matter / reply_hr 選択時のみ表示 */
  proposalInfo: string;
  /**
   * 提案情報変更ハンドラ
   * @param {string} value 新しい提案情報
   * @return {void}
   */
  onProposalInfoChange: (value: string) => void;
  /**
   * 生成を実行する. custom 選択時は freeText を渡す
   * @param {PromptType} promptType 選択中のプロンプト種別
   * @param {string} [freeText] custom 時の自由入力
   * @return {void}
   */
  onGenerate: (promptType: PromptType, freeText?: string) => void;
  /**
   * 生成を停止する (任意)
   * @return {void}
   */
  onCancel?: () => void;
  /** 見出し横に出す補足 (任意. 例: スレッド全体を読んだ上で生成します) */
  hint?: string;
};

/**
 * メール返信フォーム共通の AI 文面生成セクション.
 * メール詳細・返信管理など複数フォームで同一 UI を使うための共通部品.
 * プロンプト種別のプルダウンを持ち、「カスタム指示」選択時は自由入力欄を、
 * 提案系 (reply_matter / reply_hr) 選択時はマージン・提案情報入力を表示する.
 * @param {AiComposeSectionProps} props プロンプト設定・状態・ハンドラ
 * @return {JSX.Element} AI 文面生成セクション
 */
export const AiComposeSection: FC<AiComposeSectionProps> = ({
  promptTypes,
  defaultPromptType,
  isGenerating,
  margin,
  onMarginChange,
  proposalInfo,
  onProposalInfoChange,
  onGenerate,
  onCancel,
  hint,
}) => {
  const [selected, setSelected] = useState<PromptType>(defaultPromptType);
  const [freeText, setFreeText] = useState("");

  const isCustom = selected === "custom";
  const isReplyWithProposal =
    selected === "reply_matter" || selected === "reply_hr";
  const proposalTarget: ProposalInfoTarget =
    selected === "reply_matter" ? "hr" : "matter";

  const canGenerate =
    !isGenerating && (!isCustom || freeText.trim().length > 0);

  /**
   * 生成を実行する
   * @return {void}
   */
  const handleGenerate = (): void => {
    onGenerate(selected, isCustom ? freeText.trim() || undefined : undefined);
  };

  return (
    <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700">
          <Sparkles size={12} aria-hidden />
          AI文面生成
        </span>
        {hint && (
          <span className="text-[10px] text-base-content/50">{hint}</span>
        )}
      </div>

      <Select
        size="sm"
        options={promptTypes.map(key => ({
          value: key,
          label: promptTypeLabel(key),
        }))}
        value={selected}
        onChange={value => {
          if (value) setSelected(value as PromptType);
        }}
      />

      {isCustom && (
        <Textarea
          size="sm"
          rows={2}
          value={freeText}
          onChange={e => setFreeText(e.target.value)}
          placeholder={CUSTOM_PLACEHOLDER}
          disabled={isGenerating}
          onKeyDown={e => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              if (canGenerate) handleGenerate();
            }
          }}
        />
      )}

      {isReplyWithProposal && (
        <>
          <MarginSelect
            value={margin}
            onChange={onMarginChange}
            disabled={isGenerating}
          />
          <ProposalInfoInput
            value={proposalInfo}
            onChange={onProposalInfoChange}
            target={proposalTarget}
            disabled={isGenerating}
          />
        </>
      )}

      <div className="flex justify-end items-center gap-1 pt-1">
        {isGenerating && onCancel && (
          <Button type="button" variant="ghost" size="xs" onClick={onCancel}>
            停止
          </Button>
        )}
        <Button
          type="button"
          variant="accent"
          size="sm"
          onClick={handleGenerate}
          disabled={!canGenerate}
          title={
            isCustom && freeText.trim().length === 0
              ? "プロンプトを入力してください"
              : undefined
          }
        >
          {isGenerating ? (
            <span className="inline-flex items-center gap-1">
              <Loading size="xs" />
              生成中
            </span>
          ) : (
            "生成"
          )}
        </Button>
      </div>
    </div>
  );
};
